import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Avatar, Box, Button, Chip, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useRegistrySession } from '../session/RegistrySession';
import SignedInBadge from '../session/SignedInBadge';
import { useRegistry } from '../store/RegistryStore';
import { useTenantScope } from '../store/selectors';
import { statusLabel } from '../ui/statusTones';
import { useToast } from '../ui/feedback';

export default function ManagerHeader({ menuButton }: { menuButton: React.ReactNode }) {
  const { state, dispatch } = useRegistry();
  const scope = useTenantScope();
  const toast = useToast();
  const session = useRegistrySession();
  const users = state.users.filter((user) => user.tenantId === state.currentTenantId);
  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: { xs: 1.5, md: 4 },
        py: 1.25,
      }}
    >
      <Stack
        useFlexGap
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ flexWrap: 'wrap', rowGap: 1 }}
      >
        {menuButton}
        <Chip
          size="small"
          color="warning"
          label={
            session
              ? 'Approvals, holders and allocations live · other screens sample data'
              : 'Prototype · sample data'
          }
        />
        <TextField
          select
          size="small"
          label="Tenant"
          value={state.currentTenantId}
          onChange={(event) => {
            dispatch({ type: 'SWITCH_TENANT', tenantId: event.target.value });
            toast('Switched tenant: only that manager’s funds and holders are visible');
          }}
          sx={{ minWidth: 200 }}
        >
          {state.tenants.map((tenant) => (
            <MenuItem key={tenant.id} value={tenant.id}>
              {tenant.name}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Switch user to try maker-checker. The person who submits an item cannot approve it.">
          <TextField
            select
            size="small"
            label={session ? 'Sample data viewed as' : 'Signed in as'}
            value={state.currentUserId}
            onChange={(event) => dispatch({ type: 'SWITCH_USER', userId: event.target.value })}
            sx={{ minWidth: 230 }}
            SelectProps={{
              renderValue: () => (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar
                    sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: 'primary.main' }}
                  >
                    {scope.user?.initials}
                  </Avatar>
                  <span>
                    {scope.user?.name} · {statusLabel(scope.user?.role ?? '')}
                  </span>
                </Stack>
              ),
            }}
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name} · {statusLabel(user.role)}
              </MenuItem>
            ))}
          </TextField>
        </Tooltip>
        {session ? (
          <SignedInBadge session={session} />
        ) : (
          <Button
            component={RouterLink}
            to="/investor"
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
          >
            Investor view
          </Button>
        )}
        <Tooltip title="Restore the original demo data">
          <Button
            size="small"
            color="inherit"
            startIcon={<RestartAltIcon fontSize="small" />}
            onClick={() => {
              dispatch({ type: 'RESET' });
              toast('Demo data reset', 'info');
            }}
          >
            Reset
          </Button>
        </Tooltip>
      </Stack>
    </Box>
  );
}
